// Belege nach der Zustellung (docs/belege-nach-zustellung/SPEZIFIKATION.md):
//   * Zustellnachweis – Unterschrift und Foto bei der Übergabe, additiv an sales.delivery.confirm und
//     sales.channel.reportFulfillment (`proof`), Anzeige am Lieferschein (sales.delivery.detail) und im Druck/PDF.
//   * Automatiken des Verkaufs je Betrieb – eingehende Bestellungen automatisch bestätigen (Bestell-App,
//     Telefonassistent), Lieferschein nach der Zustellung automatisch per E-Mail senden. Alle standardmäßig AUS.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CHANNEL_CHARGE_FIELD, ORDER_FIELDS } from '../lib/labels'

/**
 * Woran andere Module Kommission und Verleih an Aufträgen und Lieferscheinen erkennen (die Finanzen berechnen sie nicht
 * automatisch – das tut die Abrechnung nach der Rücknahme): Auftragsart im Zusatzfeld, Leihgebühr der Bestell-App an
 * der Zeile; Leihbuchungen stehen in rental_booking am Auftrag.
 */
export const SALES_MARKERS = { orderKindField: ORDER_FIELDS.kind, consignmentKind: 'consignment', chargeField: CHANNEL_CHARGE_FIELD, rentalCharge: 'rental', oneTimeCustomerField: ORDER_FIELDS.oneTimeCustomer } as const

// ---- Zustellnachweis -------------------------------------------------------------------------------------------------

export const PROOF_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const
export type ProofImageType = (typeof PROOF_IMAGE_TYPES)[number]
/** Größte Bilder (entschlüsselt): Unterschrift 300 KB, Foto 2 MB */
export const PROOF_LIMITS = { signatureBytes: 300 * 1024, photoBytes: 2 * 1024 * 1024 } as const
/** Rumpf der Aktionen mit Nachweis (Foto und Unterschrift als Base64 plus die übrige Meldung) – limits.bodyBytes */
export const PROOF_BODY_BYTES = 4 * 1024 * 1024
export const PROOF_KINDS = ['signature', 'photo'] as const
export type ProofKind = (typeof PROOF_KINDS)[number]
export const PROOF_SOURCES = ['ordaneo', 'api'] as const

const base64Length = (bytes: number): number => Math.ceil(bytes / 3) * 4
const sizeText = (bytes: number): string => (bytes >= 1024 * 1024 ? `${bytes / (1024 * 1024)} MB` : `${Math.round(bytes / 1024)} KB`)

const proofImageShape = (label: string, maxBytes: number) => ({
  /** Typ der Bilddaten; geprüft wird zusätzlich am Inhalt (Magic Bytes) */
  contentType: z.enum(PROOF_IMAGE_TYPES, { error: `${label}: Erlaubt sind JPEG und PNG.` }),
  /** Bilddaten als Base64 (ohne „data:…;base64,“) */
  dataBase64: z
    .string({ error: `${label}: Die Bilddaten fehlen.` })
    .min(16, { error: `${label}: Die Bilddaten fehlen.` })
    .max(base64Length(maxBytes), { error: `${label} ist zu groß (höchstens ${sizeText(maxBytes)}).` })
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, { error: `${label}: Die Bilddaten sind kein gültiges Base64 (ohne „data:“-Vorsatz).` }),
  /** Zeitpunkt der Aufnahme laut Gerät (ISO 8601); ohne Angabe der Zeitpunkt der Meldung */
  capturedAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }).optional(),
})

export const zDeliverySignatureInput = z.strictObject({
  ...proofImageShape('Die Unterschrift', PROOF_LIMITS.signatureBytes),
  /** Name der unterschreibenden Person in Druckbuchstaben (freiwillig) */
  signedBy: z.string().trim().min(1, { error: 'Bitte den Namen angeben oder das Feld weglassen.' }).max(120, { error: 'Der Name ist zu lang (höchstens 120 Zeichen).' }).optional(),
})
export const zDeliveryPhotoInput = z.strictObject(proofImageShape('Das Foto', PROOF_LIMITS.photoBytes))

/** Zustellnachweis: Unterschrift (≤ 300 KB) und/oder Foto (≤ 2 MB), je JPEG oder PNG – JPEG empfohlen */
export const zDeliveryProofInput = z.strictObject({
  signature: zDeliverySignatureInput.optional(),
  photo: zDeliveryPhotoInput.optional(),
})
export type DeliveryProofInput = z.input<typeof zDeliveryProofInput>

/** Abgelegter Nachweis am Lieferschein (Anzeige; Laden über `url` mit dem Leserecht der Lieferscheine) */
export const zDeliveryProofFile = z.object({
  fileId: zId,
  url: z.string(),
  contentType: z.string(),
  capturedAt: z.date().nullable(),
  signedBy: z.string().nullable(),
  /** ordaneo: auf der Zustellseite erfasst · api: über die Bestell-App bzw. die Schnittstelle */
  source: z.enum(PROOF_SOURCES),
})
export type DeliveryProofFile = z.output<typeof zDeliveryProofFile>

// ---- Automatiken des Verkaufs ----------------------------------------------------------------------------------------

const AUTOMATION_PERMISSION = 'sales.automation.manage'

export const zSalesAutomation = z.object({
  /** eingehende Bestellungen der Bestell-App ohne Sperre sofort bestätigen */
  autoConfirmApp: z.boolean(),
  /** eingehende Bestellungen des Telefonassistenten ohne Sperre und ohne Prüfhinweis sofort bestätigen */
  autoConfirmPhone: z.boolean(),
  /** Lieferschein nach der Zustellung (mit Unterschrift) per E-Mail an den Kunden */
  autoSendDeliveryNote: z.boolean(),
  version: z.int(),
  updatedAt: z.date().nullable(),
})
export type SalesAutomation = z.output<typeof zSalesAutomation>

export const salesAutomationGet = defineContract({
  name: 'sales.automation.get',
  title: 'Automatiken des Verkaufs anzeigen',
  description: 'Ob eingehende Bestellungen automatisch bestätigt und Lieferscheine nach der Zustellung automatisch per E-Mail versendet werden.',
  llm: {
    description:
      'Liefert die Schalter des Betriebs: Bestellungen der Bestell-App bzw. des Telefonassistenten automatisch bestätigen (Sperren wie ' +
      'Rabatt, Kreditlimit oder Prüfhinweise lassen den Auftrag weiter zur Prüfung stehen) und Lieferschein nach der Zustellung ' +
      'automatisch per E-Mail senden. Ändert nichts – geändert wird mit sales.automation.update.',
    keywords: ['automatisch bestätigen', 'Auftragseingang', 'Lieferschein per E-Mail', 'Automatik'],
  },
  input: z.strictObject({}),
  output: zSalesAutomation,
  permission: AUTOMATION_PERMISSION,
  risk: 'read',
})

export const salesAutomationUpdate = defineContract({
  name: 'sales.automation.update',
  title: 'Automatiken des Verkaufs einstellen',
  description: 'Schaltet das automatische Bestätigen eingehender Bestellungen und den Versand des Lieferscheins nach der Zustellung ein oder aus.',
  llm: {
    description:
      'Schaltet je Betrieb: `autoConfirmApp` (Bestellungen der Bestell-App), `autoConfirmPhone` (Bestellungen des Telefonassistenten) – ' +
      'bestätigt wird nur ohne Sperre (Rabatt, Kreditlimit, Hinweise der Lieferregeln, Preisabweichung, Rückruf und weitere ' +
      'Prüffälle des Auftragseingangs) – und `autoSendDeliveryNote` (Lieferschein als PDF mit Unterschrift nach der Zustellung an den Kunden). ' +
      'Ohne Angabe bleibt ein Schalter, wie er ist.',
    keywords: ['automatisch bestätigen', 'Lieferschein automatisch senden', 'Automatik einschalten'],
  },
  input: z.strictObject({
    autoConfirmApp: z.boolean().optional(),
    autoConfirmPhone: z.boolean().optional(),
    autoSendDeliveryNote: z.boolean().optional(),
    expectedVersion: z.int().optional(),
  }),
  output: zSalesAutomation,
  permission: AUTOMATION_PERMISSION,
  risk: 'write',
  assistant: 'never',
})

// ---- PDF des Lieferscheins -------------------------------------------------------------------------------------------

/** PDF eines Belegs zum Herunterladen (Base64, ohne Ablage) – dieselbe Form für Lieferschein und Rechnung */
export const zDocumentPdf = z.object({
  /** „Lieferschein-LS-2026-00042.pdf“ */
  fileName: z.string(),
  contentType: z.literal('application/pdf'),
  contentBase64: z.string(),
  sizeBytes: z.int(),
})
export type DocumentPdf = z.output<typeof zDocumentPdf>

export const deliveryPdf = defineContract({
  name: 'sales.delivery.pdf',
  title: 'Lieferschein als PDF',
  description: 'Erzeugt den Lieferschein als PDF – wie der Druck, nach der Zustellung mit Empfangsnachweis und Unterschrift.',
  llm: {
    description: 'Erzeugt den Lieferschein `id` als PDF (Base64) zum Herunterladen. Ändert nichts und legt nichts ab. Den Druck als Daten liefert sales.delivery.print.',
    keywords: ['PDF', 'Lieferschein herunterladen', 'Lieferschein als Datei'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentPdf,
  permission: 'sales.delivery.read',
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})
